import { createContext, useContext, useEffect, useRef } from "react";

import type {
  MessengerIncomingEvent,
  MessengerOutgoingEvent,
  MessengerScope,
} from "../../types/messengerEvents";

/**
 * スコープ（`project` / `shot_comment` / `dm`）と primary id を指定して
 * 受信イベントを購読するためのハンドラ型。
 *
 * `ev` は resolveScopeKey で同スコープ・同 key に該当したイベントのみが渡される。
 */
export type MessengerEventHandler = (ev: MessengerIncomingEvent) => void;

/**
 * 再接続フック。WebSocket が open → close → open と復帰した場合に呼ばれる。
 * 切断中に取りこぼした可能性があるので、購読側で一覧を REST で再取得する。
 */
export type MessengerReconnectHandler = () => void;

export interface MessengerSocketContextValue {
  /** サーバへ送信。readyState !== OPEN のときは no-op（戻り値 false）。 */
  sendEvent: (ev: MessengerOutgoingEvent) => boolean;

  /**
   * scope + key に該当する受信イベントを購読する。
   * 戻り値の関数を呼ぶと購読解除。
   */
  subscribe: (
    scope: MessengerScope,
    key: string,
    handler: MessengerEventHandler,
  ) => () => void;

  /**
   * 指定 scope の全イベントを購読する（primary id に関わらず配信）。
   * key 単位購読より先に呼ばれる。
   */
  subscribeScope: (
    scope: MessengerScope,
    handler: MessengerEventHandler,
  ) => () => void;

  /** 再接続イベントの購読。 */
  subscribeReconnect: (handler: MessengerReconnectHandler) => () => void;
}

/** 未接続時のデフォルト実装（send は no-op、subscribe は空の解除関数を返す）。 */
const defaultValue: MessengerSocketContextValue = {
  sendEvent: () => false,
  subscribe: () => () => {},
  subscribeScope: () => () => {},
  subscribeReconnect: () => () => {},
};

export const MessengerSocketContext =
  createContext<MessengerSocketContextValue>(defaultValue);

export const useMessengerSocket = (): MessengerSocketContextValue =>
  useContext(MessengerSocketContext);

/**
 * 指定 scope + key の受信イベントを購読するためのフック。
 * 内部で ref を用いるため、`handler` が毎レンダ新規生成されても購読の再登録は発生しない。
 * ハンドラ内からは最新のクロージャ（state）が参照される。
 */
export const useMessengerSubscribe = (
  scope: MessengerScope | undefined,
  key: string | undefined,
  handler: MessengerEventHandler,
): void => {
  const { subscribe } = useMessengerSocket();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!scope || !key) return;
    const proxy: MessengerEventHandler = (ev) => handlerRef.current(ev);
    const unsubscribe = subscribe(scope, key, proxy);
    return unsubscribe;
  }, [subscribe, scope, key]);
};

/**
 * 指定 scope の全受信イベントを購読するフック。
 * DM ワークフロートーストなど、特定 key に依存しないグローバルリスナー向け。
 */
export const useMessengerScopeSubscribe = (
  scope: MessengerScope | undefined,
  handler: MessengerEventHandler,
): void => {
  const { subscribeScope } = useMessengerSocket();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!scope) return;
    const proxy: MessengerEventHandler = (ev) => handlerRef.current(ev);
    const unsubscribe = subscribeScope(scope, proxy);
    return unsubscribe;
  }, [subscribeScope, scope]);
};

/** 再接続イベント購読フック（handler は ref パターンで最新参照）。 */
export const useMessengerReconnect = (handler: MessengerReconnectHandler): void => {
  const { subscribeReconnect } = useMessengerSocket();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const proxy: MessengerReconnectHandler = () => handlerRef.current();
    const unsubscribe = subscribeReconnect(proxy);
    return unsubscribe;
  }, [subscribeReconnect]);
};
